import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { Chart, registerables } from 'chart.js';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { getValueRangeMidpoint } from '../components/forms/OppModal';
import DateRangeSelector from '../components/common/DateRangeSelector';
import { getDateRange, formatDate, validateDateRange } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { monitorQuery } from '../utils/queryMonitoring';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import CompactChart from '../components/common/CompactChart';
import { useUserRole } from '../hooks/useUserRole';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, canViewAllData, getRbacMatchNames } from '../utils/permissions';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import ExportButton from '../components/common/ExportButton';
import { useUrlState } from '../hooks/useUrlState';
import { exportData, exportMultiSectionData } from '../utils/exportUtils';
import { createRepResolver } from '../utils/reportOwnership';

Chart.register(...registerables);

const OpportunityReportsPage = ({ user }) => {
    const { currentUser } = useAuth();
    const userRole = useUserRole(user);
    const { can } = usePermissions(user);
    const viewAllReports = canViewAllData(user) || can(FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS);
    const [opportunities, setOpportunities] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshingCache, setRefreshingCache] = useState(false);
    const [refreshProgress, setRefreshProgress] = useState(0);
    const refreshTimerRef = useRef(null);
    const [toast, setToast] = useState(null);
    const [range, setRange] = useUrlState('range', "30");
    const [customStart, setCustomStart] = useUrlState('customStart', "");
    const [customEnd, setCustomEnd] = useUrlState('customEnd', "");
    const [selectedReps, setSelectedReps] = useUrlState('selectedReps', []);
    // Pending filter state — all filters wait for Apply button
    const [pendingRange, setPendingRange] = useState(range || '30');
    const [pendingCustomStart, setPendingCustomStart] = useState(customStart || '');
    const [pendingCustomEnd, setPendingCustomEnd] = useState(customEnd || '');
    const [pendingReps, setPendingReps] = useState(selectedReps);
    const [showRepDropdown, setShowRepDropdown] = useState(false);
    const [usingCache, setUsingCache] = useState(false);
    const [cacheAge, setCacheAge] = useState(null);
    const [cachedData, setCachedData] = useState(null);
    const repDropdownRef = useRef(null);
    
    // Chart configurations for CompactChart components
    const [pipelineChartConfig, setPipelineChartConfig] = useState(null);
    const [conversionChartConfig, setConversionChartConfig] = useState(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (repDropdownRef.current && !repDropdownRef.current.contains(event.target)) {
                setShowRepDropdown(false);
            }
        };

        if (showRepDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showRepDropdown]);

    // Keep drafts in sync with browser navigation, not dropdown visibility.
    useEffect(() => {
        setPendingReps(selectedReps);
    }, [selectedReps]);



    // Apply rep filter changes (from dropdown)
    const handleApplyRepFilter = () => {
        setShowRepDropdown(false);
    };

    // Cancel rep filter changes
    const handleCancelRepFilter = () => {
        setPendingReps(selectedReps);
        setShowRepDropdown(false);
    };

    // Detect when any pending filter differs from applied
    const filtersChanged = useMemo(() => {
        const repChanged = JSON.stringify(pendingReps) !== JSON.stringify(selectedReps);
        const dateChanged = pendingRange !== range || pendingCustomStart !== customStart || pendingCustomEnd !== customEnd;
        return repChanged || dateChanged;
    }, [pendingReps, selectedReps, pendingRange, range, pendingCustomStart, customStart, pendingCustomEnd, customEnd]);

    const hasActiveFilters = useMemo(() => {
        return pendingRange !== '30' || pendingCustomStart || pendingCustomEnd || pendingReps.length > 0;
    }, [pendingRange, pendingCustomStart, pendingCustomEnd, pendingReps]);

    // Apply all filters — copies pending to URL state (batched via microtask)
    const handleApplyFilters = () => {
        setRange(pendingRange);
        setCustomStart(pendingCustomStart);
        setCustomEnd(pendingCustomEnd);
        setSelectedReps(pendingReps);
    };

    const handleClearFilters = () => {
        setPendingRange('30');
        setPendingCustomStart('');
        setPendingCustomEnd('');
        setPendingReps([]);
        setRange('30');
        setCustomStart('');
        setCustomEnd('');
        setSelectedReps([]);
    };

    // Sync pending state from URL on external changes (browser back/forward)
    useEffect(() => { setPendingRange(range || '30'); }, [range]);
    useEffect(() => { setPendingCustomStart(customStart || ''); }, [customStart]);
    useEffect(() => { setPendingCustomEnd(customEnd || ''); }, [customEnd]);

    useEffect(() => {
        if (!user || !user.uid) return;
        if (userRole === undefined || userRole === null) return; // Wait for role to load

        const fetchData = async () => {
            setLoading(true);
            try {
                console.log('[OpportunityReports] Fetching with userRole:', userRole);

                // Validate date range before fetching
                const validation = validateDateRange(range, customStart, customEnd);
                if (!validation.isValid) {
                    setToast({ message: validation.error, type: 'error' });
                    setLoading(false);
                    return;
                }

                // Get date range
                const { start, end } = getDateRange(range, customStart, customEnd);
                const isStandardRange = ['7','14', '30', '90', '180', '365', 'ytd'].includes(range);
                const hasRepFilter = selectedReps.length > 0;

                // Try to use cached data for standard ranges without rep filter
                if (isStandardRange && !hasRepFilter) {
                    const cacheId = range === 'ytd' 
                        ? 'summary_ytd' 
                        : `summary_${range.padStart(2, '0')}d`; // Pad with zero (e.g., '7' -> '07d')

                    try {
                        const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), cacheId));
                        
                        if (cacheDoc.exists()) {
                            const data = cacheDoc.data();
                            const now = new Date();
                            const lastUpdated = data.lastUpdated?.toDate?.() || new Date(0);
                            const ageMinutes = Math.floor((now - lastUpdated) / (1000 * 60));
                            
                            // Use cache if less than 12 hours old (generation runs every 8 hours)
                            if (ageMinutes < 720) {
                                console.log(`[OpportunityReports] ✅ Using cached data (${ageMinutes} min old, cache ID: ${cacheId})`);
                                setCachedData(data);
                                setUsingCache(true);
                                setCacheAge(ageMinutes);
                                
                                // Still fetch opportunities for charts (cache doesn't have individual records)
                                const oppsSnap = await monitorQuery('fetch-opportunities-opp-reports-cache', () =>
                                    getDocs(collection(db, collectionPath('opportunities')))
                                );
                                let oppsData = oppsSnap.docs.map(doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                }));
                                
                                // RBAC: Non-admin users only see their own opportunities (even with cache)
                                // Wait for userRole to load before filtering
                                if (!viewAllReports && !can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
                                    const matchNames = getRbacMatchNames(user);
                                    const matchSet = new Set(matchNames);
                                    const beforeFilter = oppsData.length;
                                    oppsData = oppsData.filter(opp => {
                                        // Match by uid first
                                        if (opp.uid === user.uid) return true;
                                        // Fallback: match by customerName's salesRep or opp owner displayName
                                        if (opp.salesRepName && matchSet.has(opp.salesRepName.toLowerCase().trim())) return true;
                                        return false;
                                    });
                                    console.log('[OpportunityReports] Cache RBAC filtered:', {
                                        before: beforeFilter,
                                        after: oppsData.length,
                                        userUid: user.uid,
                                        matchNames,
                                        userRole
                                    });
                                }
                                
                                setOpportunities(oppsData);
                                
                                // Fetch users for rep filter dropdown
                                const usersSnap = await monitorQuery('fetch-users-opp-reports-cache', () =>
                                    getDocs(collection(db, collectionPath('users')))
                                );
                                const usersData = usersSnap.docs.map(doc => ({
                                    uid: doc.id,
                                    ...doc.data()
                                }));
                                setUsers(usersData);
                                
                                setLoading(false);
                                return;
                            } else {
                            }
                        }
                    } catch (cacheErr) {
                        console.warn('[OpportunityReports] Cache fetch failed, using live data:', cacheErr);
                    }
                }

                // Fetch live data
                setUsingCache(false);
                setCacheAge(null);
                setCachedData(null);

                // Fetch opportunities
                const oppsSnap = await getDocs(collection(db, collectionPath('opportunities')));
                let oppsData = oppsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                // RBAC: Non-admin users only see their own opportunities
                // Wait for userRole to load before filtering
                if (!viewAllReports && !can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
                    const matchNames = getRbacMatchNames(user);
                    const matchSet = new Set(matchNames);
                    const beforeFilter = oppsData.length;
                    oppsData = oppsData.filter(opp => {
                        if (opp.uid === user.uid) return true;
                        if (opp.salesRepName && matchSet.has(opp.salesRepName.toLowerCase().trim())) return true;
                        return false;
                    });
                    console.log('[OpportunityReports] Live RBAC filtered:', {
                        before: beforeFilter,
                        after: oppsData.length,
                        userUid: user.uid,
                        matchNames,
                        userRole
                    });
                }
                
                setOpportunities(oppsData);

                // Fetch users
                const usersSnap = await getDocs(collection(db, collectionPath('users')));
                const usersData = usersSnap.docs.map(doc => ({
                    uid: doc.id,
                    ...doc.data()
                }));
                setUsers(usersData);

                setLoading(false);
            } catch (error) {
                console.error('[OpportunityReports] Error fetching data:', error);
                setToast({ message: `Error loading data: ${error.message}`, type: 'error' });
                setLoading(false);
            }
        };

        fetchData();
    }, [user, range, customStart, customEnd, selectedReps, userRole, can, viewAllReports]);

    // Manual cache refresh
    const handleRefreshCache = async () => {
        // Check cache age - prevent refresh if data is too recent (< 10 minutes)
        if (cachedData && cachedData.lastUpdated) {
            const cacheAge = Date.now() - cachedData.lastUpdated.toDate().getTime();
            const minRefreshInterval = 5 * 60 * 1000; // 5 minutes in milliseconds
            
            if (cacheAge < minRefreshInterval) {
                const remainingTime = Math.ceil((minRefreshInterval - cacheAge) / 60000);
                setToast({ 
                    message: `Cache was refreshed recently. Please wait ${remainingTime} more minute(s) before refreshing again.`, 
                    type: 'error' 
                });
                return;
            }
        }

        const confirmed = window.confirm(
            'This will refresh the reports cache for all date ranges. ' +
            'It may take 1-2 minutes. Continue?'
        );
        
        if (!confirmed) return;

        try {
            setRefreshingCache(true);
            setRefreshProgress(0);
            
            // Start progress timer (simulate progress)
            let progress = 0;
            refreshTimerRef.current = setInterval(() => {
                progress += 1;
                if (progress <= 90) { // Cap at 90% until actual completion
                    setRefreshProgress(progress);
                }
            }, 1000); // Update every second
            
            setToast({ message: 'Refreshing cache... Please wait.', type: 'success' });
            
            const refreshCacheFunc = httpsCallable(functions, 'refreshReportsCache');
            await refreshCacheFunc();
            
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            setRefreshProgress(100);
            
            setToast({ message: 'Cache refreshed successfully! Reloading data...', type: 'success' });
            
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch (error) {
            // Clear progress timer
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            
            console.error('[OpportunityReports] Error refreshing cache:', error);
            setRefreshingCache(false);
            setRefreshProgress(0);
            
            let errorMessage = 'Error refreshing cache';
            if (error.code === 'unauthenticated') {
                errorMessage = 'Please sign in to refresh the cache';
            } else if (error.code === 'permission-denied') {
                errorMessage = 'You do not have permission to refresh the cache';
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }
            
            setToast({ message: errorMessage, type: 'error' });
        }
    };

    // Date range filtering
    const { start: filterStart, end: filterEnd } = useMemo(() => 
        getDateRange(range, customStart, customEnd), 
        [range, customStart, customEnd]
    );

    // Filter opportunities by date range
    const filteredOpps = useMemo(() => {
        const filtered = opportunities.filter(opp => {
            const oppDate = opp.startDate?.toDate?.() 
                ? opp.startDate.toDate() 
                : opp.startDate 
                    ? new Date(opp.startDate)
                    : null;
            
            if (!oppDate) {
                return false;
            }
            
            const inRange = oppDate >= filterStart && oppDate <= filterEnd;
            return inRange;
        });
        
        return filtered;
    }, [opportunities, filterStart, filterEnd]);

    const repResolver = useMemo(() => createRepResolver(users), [users]);

    // Filter by selected reps
    const filteredOppsByRep = useMemo(() => {
        if (selectedReps.length === 0) {
            return filteredOpps;
        }

        const filtered = filteredOpps.filter(opp => {
            return repResolver.matches([repResolver.opportunityRep(opp)], selectedReps);
        });
        
        return filtered;
    }, [filteredOpps, selectedReps, repResolver]);

    // Get list of all reps for dropdown
    const allReps = useMemo(() => {
        return repResolver.options(opportunities.map(repResolver.opportunityRep));
    }, [opportunities, repResolver]);

    // Calculate opportunity metrics
    const oppMetrics = useMemo(() => {
        // RBAC: Non-admin users must recalculate from filtered opportunities (don't use cached summary)
        // Use cached summary if available, no rep filter, AND admin
        if (usingCache && cachedData && cachedData.summary && selectedReps.length === 0 && viewAllReports) {
            const summary = cachedData.summary;
            const closedTotal = (summary.wonOpportunities || 0) + (summary.lostOpportunities || 0);
            const winRate = closedTotal > 0 ? ((summary.wonOpportunities / closedTotal) * 100).toFixed(1) : 0;
            
            return {
                total: summary.totalOpportunities || 0,
                totalValue: summary.pipelineValue || 0,
                won: summary.wonOpportunities || 0,
                lost: summary.lostOpportunities || 0,
                active: summary.activeOpportunities || 0,
                winRate: winRate,
                newAccountCount: summary.newAccountCount || 0,
                newAccountValue: summary.newAccountValue || 0,
                revenueForecast: summary.revenueForecast || 0
            };
        }

        // Calculate from live data
        const oppsToUse = selectedReps.length > 0 ? filteredOppsByRep : filteredOpps;

        const metrics = {
            total: oppsToUse.length,
            totalValue: 0,
            won: 0,
            lost: 0,
            active: 0,
            winRate: 0,
            newAccountCount: 0,
            newAccountValue: 0,
            revenueForecast: 0
        };

        // Stage-based win probability for forecast
        const stageProbability = {
            'New': 0.10,
            'Qualified': 0.25,
            'Not Qualified': 0,        // Disqualified - 0% probability
            'Proposal Sent': 0.75,
            'Closed Won': 1.0,
            'Closed Lost': 0
        };

        oppsToUse.forEach(opp => {
            // Use actualValue for Closed Won if available, otherwise use midpoint
            const oppValue = opp.stage === 'Closed Won' 
                ? getValueRangeMidpoint(opp.value, opp.actualValue)
                : getValueRangeMidpoint(opp.value);
            metrics.totalValue += oppValue;
            
            // Track new account opportunities
            if (opp.isNewAccount) {
                metrics.newAccountCount++;
                metrics.newAccountValue += oppValue;
            }
            
            if (opp.stage === 'Closed Won') {
                metrics.won++;
                // Won deals are 100% in forecast
                metrics.revenueForecast += oppValue;
            } else if (opp.stage === 'Closed Lost') {
                metrics.lost++;
                // Lost deals don't contribute to forecast
            } else {
                metrics.active++;
                // Active deals contribute based on stage probability
                const prob = stageProbability[opp.stage] || 0.25;
                metrics.revenueForecast += oppValue * prob;
            }
        });

        const closedTotal = metrics.won + metrics.lost;
        metrics.winRate = closedTotal > 0 ? ((metrics.won / closedTotal) * 100).toFixed(1) : 0;

        return metrics;
    }, [filteredOpps, filteredOppsByRep, selectedReps, usingCache, cachedData, viewAllReports]);

    // Pipeline value by stage
    const pipelineByStage = useMemo(() => {
        // RBAC: Non-admin users must recalculate from filtered opportunities
        // Use cached data if available, no rep filter, AND admin
        if (usingCache && cachedData && cachedData.pipelineByStage && selectedReps.length === 0 && viewAllReports) {
            return cachedData.pipelineByStage;
        }

        const oppsToUse = selectedReps.length > 0 ? filteredOppsByRep : filteredOpps;
        const stageData = {};

        oppsToUse.forEach(opp => {
            const stage = opp.stage || 'Unknown';
            if (!stageData[stage]) stageData[stage] = 0;
            // Use actualValue for Closed Won if available
            stageData[stage] += opp.stage === 'Closed Won' 
                ? getValueRangeMidpoint(opp.value, opp.actualValue)
                : getValueRangeMidpoint(opp.value);
        });

        return stageData;
    }, [filteredOpps, filteredOppsByRep, selectedReps, usingCache, cachedData, viewAllReports]);

    // Conversion funnel data
    const conversionFunnel = useMemo(() => {
        // RBAC: Non-admin users must recalculate from filtered opportunities
        // Use cached data if available, no rep filter, AND admin
        if (usingCache && cachedData && cachedData.conversionFunnel && selectedReps.length === 0 && viewAllReports) {
            return cachedData.conversionFunnel;
        }

        const oppsToUse = selectedReps.length > 0 ? filteredOppsByRep : filteredOpps;

        const funnel = {
            totalOpps: oppsToUse.length,
            activeOpps: oppsToUse.filter(o => !['Closed Won', 'Closed Lost'].includes(o.stage)).length,
            wonOpps: oppsToUse.filter(o => o.stage === 'Closed Won').length,
            lostOpps: oppsToUse.filter(o => o.stage === 'Closed Lost').length
        };

        return funnel;
    }, [filteredOpps, filteredOppsByRep, selectedReps, usingCache, cachedData, viewAllReports]);

    // Prepare pipeline chart configuration
    useEffect(() => {
        
        // Only create chart config if there's data
        const labels = Object.keys(pipelineByStage);
        const data = Object.values(pipelineByStage);
        
        // Count opportunities per stage for display
        const oppsToUse = selectedReps.length > 0 ? filteredOppsByRep : filteredOpps;
        const stageCounts = {};
        oppsToUse.forEach(opp => {
            const stage = opp.stage || 'Unknown';
            stageCounts[stage] = (stageCounts[stage] || 0) + 1;
        });
        
        // Create labels with counts: "Stage (count)"
        const labelsWithCounts = labels.map(label => {
            const count = stageCounts[label] || 0;
            return `${label} (${count})`;
        });
                
        if (labels.length === 0 || data.every(val => val === 0)) {
            setPipelineChartConfig(null);
            return;
        }

        // Custom color mapping for opportunity stages
        const getStageColor = (stage) => {
            const stageColors = {                
                'New': '#00eeffff',           // Teal
                'Qualified': '#3B82F6',       // Blue  
                'Not Qualified': '#9CA3AF',   // Gray - disqualified
                'Proposal Sent': '#8B5CF6',   // Purple
                'Closed Won': '#059669',      // Dark Green
                'Closed Lost': '#EF4444'      // Red
            };
            return stageColors[stage] || '#6B7280'; // Default gray for unknown stages
        };

        const backgroundColor = labels.map(label => getStageColor(label));

        setPipelineChartConfig({
            type: 'doughnut',
            data: {
                labels: labelsWithCounts,
                datasets: [{
                    data: data,
                    backgroundColor: backgroundColor,
                    borderWidth: 2,
                    borderColor: '#ffffff'
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Pipeline Value by Stage',
                        font: { size: 16 }
                    },
                    legend: {
                        position: 'right'
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                const value = context.parsed;
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                return `${context.label}: $${value.toLocaleString()} (${percentage}%)`;
                            }
                        }
                    }
                }
            }
        });
    }, [pipelineByStage, filteredOpps, filteredOppsByRep, selectedReps]);

    // Prepare conversion funnel chart configuration
    useEffect(() => {
        // Only create chart config if there's data
        if (!conversionFunnel || conversionFunnel.totalOpps === 0) {
            setConversionChartConfig(null);
            return;
        }

        setConversionChartConfig({
            type: 'bar',
            data: {
                labels: ['Total Opportunities', 'Active', 'Won', 'Lost'],
                datasets: [{
                    label: 'Count',
                    data: [
                        conversionFunnel.totalOpps,
                        conversionFunnel.activeOpps,
                        conversionFunnel.wonOpps,
                        conversionFunnel.lostOpps
                    ],
                    backgroundColor: ['#3B82F6', '#EAB308', '#059669', '#EF4444'], // Blue, Yellow, Dark Green, Red
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: true,
                        text: 'Opportunity Conversion Funnel',
                        font: { size: 16 }
                    },
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                return `${context.label}: ${context.parsed.y}`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1,
                            precision: 0
                        }
                    }
                }
            }
        });
    }, [conversionFunnel]);

    const exportSections = [
        { key: 'pipeline', label: 'Pipeline by Stage' },
        { key: 'metrics', label: 'Opportunity Metrics' }
    ];

    const handleExport = (format, sectionKey) => {
        const dateLabel = range === 'ytd' ? 'YTD' : `${range}d`;

        // Pipeline by Stage section
        const stages = Object.keys(pipelineByStage);
        const oppsToUse = selectedReps.length > 0 ? filteredOppsByRep : filteredOpps;
        const stageCounts = {};
        oppsToUse.forEach(opp => {
            const stage = opp.stage || 'Unknown';
            stageCounts[stage] = (stageCounts[stage] || 0) + 1;
        });
        const pipelineData = stages.map(stage => ({ stage, value: pipelineByStage[stage], count: stageCounts[stage] || 0 }));
        pipelineData.push({ stage: 'TOTAL', value: oppMetrics.totalValue, count: oppMetrics.total });
        const pipelineHeaders = ['Stage', 'Pipeline Value', 'Opportunity Count'];
        const pipelineRowMapper = (row) => [
            row.stage,
            `$${(row.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            row.count
        ];

        // Metrics summary section
        const metricsHeaders = ['Metric', 'Value'];
        const metricsData = [
            { metric: 'Total Opportunities', value: oppMetrics.total },
            { metric: 'Pipeline Value', value: `$${(oppMetrics.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { metric: 'Won', value: oppMetrics.won },
            { metric: 'Lost', value: oppMetrics.lost },
            { metric: 'Active', value: oppMetrics.active },
            { metric: 'Win Rate', value: `${oppMetrics.winRate}%` },
            { metric: 'New Account Opps', value: oppMetrics.newAccountCount },
            { metric: 'New Account Value', value: `$${(oppMetrics.newAccountValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { metric: 'Revenue Forecast', value: `$${(oppMetrics.revenueForecast || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
        ];
        const metricsRowMapper = (row) => [row.metric, typeof row.value === 'number' ? row.value.toLocaleString() : row.value];

        if (sectionKey === 'pipeline') {
            exportData({
                filename: `opportunity_pipeline_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: pipelineHeaders, data: pipelineData, rowMapper: pipelineRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Pipeline by Stage (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
                    : { sheetName: 'Pipeline by Stage', columnWidths: [20, 18, 18] }
            });
        } else if (sectionKey === 'metrics') {
            exportData({
                filename: `opportunity_metrics_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format, headers: metricsHeaders, data: metricsData, rowMapper: metricsRowMapper,
                options: format.toLowerCase() === 'pdf'
                    ? { title: `Opportunity Metrics (${dateLabel})`, fontSize: 9, orientation: 'portrait', pageSize: 'a4' }
                    : { sheetName: 'Opportunity Metrics', columnWidths: [25, 20] }
            });
        } else {
            exportMultiSectionData({
                filename: `opportunity_reports_all_${dateLabel}_${new Date().toISOString().split('T')[0]}`,
                format,
                sections: [
                    { name: 'Pipeline by Stage', headers: pipelineHeaders, data: pipelineData, rowMapper: pipelineRowMapper, columnWidths: [20, 18, 18] },
                    { name: 'Opportunity Metrics', headers: metricsHeaders, data: metricsData, rowMapper: metricsRowMapper, columnWidths: [25, 20] }
                ],
                options: { title: `Opportunity Reports (${dateLabel})`, fontSize: 9, orientation: 'landscape', pageSize: 'a4' }
            });
        }
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading opportunity reports...</div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            
            {/* Cache Refresh Progress Overlay */}
            {refreshingCache && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
                        <div className="text-center">
                            <div className="mb-4">
                                <svg className="animate-spin h-12 w-12 text-amber-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Refreshing Cache</h3>
                            <p className="text-gray-600 mb-4">
                                Generating reports for all date ranges...
                            </p>
                            <div className="mb-2">
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div 
                                        className="bg-amber-600 h-2.5 rounded-full transition-all duration-1000" 
                                        style={{ width: `${refreshProgress}%` }}
                                    ></div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-500">
                                {refreshProgress < 30 
                                    ? `${refreshProgress}s - Fetching opportunities from Firestore...` 
                                    : refreshProgress < 60
                                    ? `${refreshProgress}s - Processing opportunity data...`
                                    : refreshProgress < 90
                                    ? `${refreshProgress}s - Calculating analytics and generating cache...`
                                    : 'Finalizing cache generation...'}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Opportunity Reports</h2>
                    <p className="text-gray-600">Analyze pipeline value, conversion rates, and win probability</p>
                </div>
                {can(FEATURE_PERMISSIONS.REPORTS_EXPORT) && (
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={loading || Object.keys(pipelineByStage).length === 0}
                        tooltipText="Export opportunity report data"
                        label="Export"
                        className="bg-indigo-600 hover:bg-indigo-700"
                        sections={exportSections}
                    />
                )}
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    {/* Sales Rep Filter */}
                    {allReps.length > 0 && (
                        <div className="w-full md:w-64">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Filter by Sales Rep
                            </label>
                            <div className="relative" ref={repDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowRepDropdown(!showRepDropdown)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-left flex items-center justify-between"
                                >
                                    <span className="block truncate">
                                        {pendingReps.length === 0 
                                            ? 'All Sales Reps' 
                                            : pendingReps.length === allReps.length
                                            ? 'All Sales Reps'
                                            : `${pendingReps.length} selected`}
                                    </span>
                                    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>

                    {showRepDropdown && (
                        <div className="absolute z-50 mt-2 w-80 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                            <div className="p-3 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-gray-700">Select Sales Reps</span>
                                    <button
                                        onClick={() => {
                                            if (pendingReps.length === allReps.length) {
                                                setPendingReps([]);
                                            } else {
                                                setPendingReps(allReps.map(r => r.name));
                                            }
                                        }}
                                        className="text-xs text-amber-600 hover:text-amber-800"
                                    >
                                        {pendingReps.length === allReps.length ? 'Clear All' : 'Select All'}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    {pendingReps.length} of {allReps.length} selected
                                </p>
                            </div>
                            
                            <div className="p-2">
                                {allReps.map(rep => (
                                    <label
                                        key={rep.name}
                                        className="flex items-center px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={repResolver.matches([rep.name], pendingReps)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setPendingReps([...pendingReps, rep.name]);
                                                } else {
                                                    setPendingReps(pendingReps.filter(r => !repResolver.matches([rep.name], [r])));
                                                }
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">{rep.name}</span>
                                    </label>
                                ))}
                            </div>

                            <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
                                <button
                                    onClick={handleCancelRepFilter}
                                    className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleApplyRepFilter}
                                    className="px-3 py-1.5 text-sm text-white bg-amber-600 rounded hover:bg-amber-700"
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    )}
                            </div>
                        </div>
                    )}

                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Date
                        </label>
                        <DateRangeSelector
                            range={pendingRange}
                            setRange={setPendingRange}
                            customStart={pendingCustomStart}
                            setCustomStart={setPendingCustomStart}
                            customEnd={pendingCustomEnd}
                            setCustomEnd={setPendingCustomEnd}
                            hideClear
                            defaultRange="30"
                        />
                    </div>

                    {/* Apply and Clear Buttons */}
                    <div className="flex items-end gap-2">
                        <button
                            onClick={handleApplyFilters}
                            disabled={!filtersChanged}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                filtersChanged
                                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {filtersChanged ? '✓ Apply Filters' : 'Applied'}
                        </button>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Cache indicator */}
            {usingCache && cachedData && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-green-800">
                            Using cached data (last updated {cacheAge}m ago)
                        </span>
                    </div>
                    {can(FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH) && (
                        <button
                            onClick={handleRefreshCache}
                            disabled={refreshingCache}
                            className={`text-sm font-medium px-3 py-1.5 rounded flex items-center gap-2 ${
                                refreshingCache 
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                                    : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                            {refreshingCache ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Refreshing...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    <span>Refresh Cache</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}

            {/* Opportunity Metrics Cards - Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Tooltip text="Total number of opportunities in the selected date range.">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Total Opportunities</div>
                        <div className="text-3xl font-bold text-amber-600">{oppMetrics.total}</div>
                    </div>
                </Tooltip>

                <Tooltip text="Combined value of all opportunities in pipeline.">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Pipeline Value</div>
                        <div className="text-3xl font-bold text-blue-600">
                            ${oppMetrics.totalValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                    </div>
                </Tooltip>

                <Tooltip text="Opportunities marked as Closed Won in the selected period.">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Won</div>
                        <div className="text-3xl font-bold text-green-600">{oppMetrics.won}</div>
                    </div>
                </Tooltip>

                <Tooltip text="Percentage of closed opportunities that were won (won / (won + lost)).">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <div className="text-sm text-gray-600 mb-1">Win Rate</div>
                        <div className="text-3xl font-bold text-green-600">{oppMetrics.winRate}%</div>
                    </div>
                </Tooltip>
            </div>

            {/* Opportunity Metrics Cards - Row 2: New Account & Forecast */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <Tooltip text="Opportunities from new accounts (inline-created customers, leads, or customers created within 30 days of the opportunity). Filtered by the selected date range.">
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                        <div className="text-sm text-gray-600 mb-1">New Account Opps</div>
                        <div className="text-3xl font-bold text-purple-600">{oppMetrics.newAccountCount}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            ${oppMetrics.newAccountValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} value
                        </div>
                    </div>
                </Tooltip>

                <Tooltip text="Weighted revenue forecast based on stage probability. New: 10%, Qualified: 25%, Proposal Sent: 75%, Won: 100%. Not Qualified: 0% (disqualified).">
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-indigo-500">
                        <div className="text-sm text-gray-600 mb-1">Revenue Forecast</div>
                        <div className="text-3xl font-bold text-indigo-600">
                            ${oppMetrics.revenueForecast.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">weighted by stage probability</div>
                    </div>
                </Tooltip>

                <Tooltip text="Active opportunities not yet won or lost.">
                    <div className="bg-white p-6 rounded-lg shadow border-l-4 border-amber-500">
                        <div className="text-sm text-gray-600 mb-1">Active Pipeline</div>
                        <div className="text-3xl font-bold text-amber-600">{oppMetrics.active}</div>
                        <div className="text-xs text-gray-500 mt-1">
                            {oppMetrics.lost} lost this period
                        </div>
                    </div>
                </Tooltip>
            </div>

            {/* Charts Grid - Compact Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Pipeline Value by Stage Chart */}
                {pipelineChartConfig ? (
                    <CompactChart
                        chartConfig={pipelineChartConfig}
                        title="Pipeline Value by Stage"
                        height="h-64"
                        tooltip="Shows total pipeline value broken down by opportunity stage. Hover to see dollar amounts and percentages."
                    />
                ) : (
                    <div className="bg-white p-6 rounded-lg shadow">
                        <Tooltip text="Shows total pipeline value broken down by opportunity stage. Hover to see dollar amounts and percentages.">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Pipeline Value by Stage</h3>
                        </Tooltip>
                        <div className="h-64 flex items-center justify-center">
                            <div className="text-gray-500 text-center">
                                <div className="text-4xl mb-2">📊</div>
                                <div>No pipeline data available</div>
                                <div className="text-sm mt-1">Create some opportunities to see this chart or change the filters.</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Opportunity Conversion Funnel Chart */}
                {conversionChartConfig ? (
                    <CompactChart
                        chartConfig={conversionChartConfig}
                        title="Opportunity Conversion Funnel"
                        height="h-64"
                        tooltip="Visualizes opportunity progression from initial contact to closed status. Shows conversion funnel at a glance."
                    />
                ) : (
                    <div className="bg-white p-6 rounded-lg shadow">
                        <Tooltip text="Visualizes opportunity progression from initial contact to closed status. Shows conversion funnel at a glance.">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Opportunity Conversion Funnel</h3>
                        </Tooltip>
                        <div className="h-64 flex items-center justify-center">
                            <div className="text-gray-500 text-center">
                                <div className="text-4xl mb-2">🎯</div>
                                <div>No conversion data available</div>
                                <div className="text-sm mt-1">Create some opportunities to see this funnel or change the filters.</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Win Probability Placeholder */}
            <div className="bg-gradient-to-br from-amber-500 to-amber-700 p-8 rounded-lg shadow-lg text-white">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-2xl font-bold mb-2">Opportunity Value with Win Probability</h3>
                        <p className="text-amber-100 mb-4">
                            AI-powered win probability scoring based on historical patterns
                        </p>
                        <div className="text-sm text-amber-200">
                            🎯 Coming in Phase 2 - Machine learning models to predict deal outcomes
                        </div>
                    </div>
                    <div className="text-6xl opacity-20">🔮</div>
                </div>
            </div>
        </main>
    );
};

export default OpportunityReportsPage;
